import { ChevronLeft as ChevronLeftIcon } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import { ChangeLogRow } from 'components/BlocksList/ChangeLogRow';
import {
  blocksCompletedActionStatuses,
  blocksPendingActionStatuses,
  changeLogHeaders,
  changeLogTypes,
} from 'components/BlocksList/constants';
import { Spinner } from 'components/Loading/Spinner/Spinner';
import { Card } from 'components/TeamOverview/components/Common/Card/Card';
import { useOrdersTabContentStyles } from 'components/UserProfile/styles';
import React, { useEffect, useState } from 'react';
import {
  useContributedBlocksLogQuery,
  useDisposedBlocksLogQuery,
} from 'store/apis/teamsApi';
import { formatDateForChangeLog } from 'utilities/dates_and_times';

export const ChangeLog = ({
  handleClose,
  teamUUID,
  projects,
  type = changeLogTypes.CONFIRMED,
}) => {
  const { classes } = useOrdersTabContentStyles();
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);

  const { data: disposedBlocks, isLoading: isDisposedBlocksLoading } =
    useDisposedBlocksLogQuery(
      { teamUUID },
      { refetchOnMountOrArgChange: true },
    );

  const { data: contributedBlocks, isLoading: isContributedBlocksLoading } =
    useContributedBlocksLogQuery(
      { teamUUID },
      { refetchOnMountOrArgChange: true },
    );

  const isLoading = isDisposedBlocksLoading || isContributedBlocksLoading;

  useEffect(() => {
    if (!contributedBlocks || !disposedBlocks) return;

    const allBlocks = [
      ...contributedBlocks.map((block) => ({
        ...block,
        action: 'contribution',
      })),
      ...disposedBlocks.map((block) => ({ ...block, action: 'disposal' })),
    ]
      .sort((a, b) => {
        const aDate = a[`${a.action}_request_datetime`];
        const bDate = b[`${b.action}_request_datetime`];

        return new Date(bDate) - new Date(aDate);
      })
      .map((block) => {
        const projectName = projects
          ? projects.find((project) =>
            project.orders?.find((order) =>
              order.name.includes(block.original_sample?.order_id),
            ),
          )?.name
          : '';

        const date = formatDateForChangeLog(
          block[`${block.action}_request_datetime`],
        );

        return {
          ...block,
          projectName,
          date,
        };
      });

    setRows(allBlocks);
  }, [disposedBlocks, contributedBlocks, projects]);

  useEffect(() => {
    if (!rows.length) {
      setFilteredRows([]);

      return;
    }

    const pendingStatuses = Object.values(blocksPendingActionStatuses);
    const completedStatuses = Object.values(blocksCompletedActionStatuses);
    const validStatuses =
      type === changeLogTypes.CONFIRMED ? completedStatuses : pendingStatuses;

    const filtered = rows.filter((row) =>
      validStatuses.includes(row.ship_back_client_state),
    );
    setFilteredRows(filtered);
  }, [rows, type]);

  return (
    <Card
      header={
        <Box display="flex" justifyContent="space-between" width="100%">
          <Box onClick={handleClose} className={classes.backContainer}>
            <ChevronLeftIcon fontSize="large" />
            <Typography className={classes.backLabel}>BACK</Typography>
            <Typography className={classes.cardTitle}>
              {type === changeLogTypes.CONFIRMED
                ? 'Change Log'
                : 'Pending Requests'}
            </Typography>
          </Box>
        </Box>
      }
    >
      <Box className={classes.bodyColumn}>
        <Grid container className={classes.container}>
          {changeLogHeaders.map(({ value, xs }) => (
            <Grid xs={xs} item key={value}>
              <Typography
                fontSize="0.875rem"
                className={classes.orderRowHeader}
              >
                {value}
              </Typography>
            </Grid>
          ))}
        </Grid>
        <Box className={classes.ordersList} sx={{ minWidth: 600 }}>
          {isLoading && (
            <Box className={classes.spinnerContainer}>
              <Spinner />
            </Box>
          )}
          {!isLoading && filteredRows.length ? (
            filteredRows.map((block) => (
              <ChangeLogRow
                key={`${block.disposal_request_datetime}-${block.name}`}
                block={block}
              />
            ))
          ) : (
            <Box height="100%" display="flex" alignItems="center">
              <Typography>No events</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
};
